/**
 * Product Page JavaScript
 * Handles variant selection, quantity controls, image lightbox, and add to cart
 */

(function () {
  "use strict";

  /**
   * Image Lightbox Class
   * Handles opening/closing product images in a modal view
   */
  class ProductLightbox {
    constructor() {
      this.lightbox = document.getElementById("product-lightbox");
      this.lightboxImage = document.getElementById("lightbox-image");
      this.lightboxCount = document.getElementById("lightbox-count");
      this.currentIndex = 0;
      this.images = [];

      if (!this.lightbox) return;

      this.init();
    }

    init() {
      // Collect all product images
      const imageItems = document.querySelectorAll(".product__media-item img[data-lightbox-src]");
      imageItems.forEach((img, index) => {
        this.images.push({
          src: img.dataset.lightboxSrc || img.src,
          alt: img.dataset.lightboxAlt || img.alt || "Product image",
        });
      });

      // Add click handlers to images
      imageItems.forEach((img, index) => {
        img.addEventListener("click", () => this.open(index));
      });

      // Close handlers
      const closeButtons = document.querySelectorAll("[data-lightbox-close]");
      closeButtons.forEach((btn) => {
        btn.addEventListener("click", () => this.close());
      });

      // Navigation handlers
      const prevButton = document.querySelector("[data-lightbox-prev]");
      const nextButton = document.querySelector("[data-lightbox-next]");

      if (prevButton) {
        prevButton.addEventListener("click", () => this.prev());
      }

      if (nextButton) {
        nextButton.addEventListener("click", () => this.next());
      }

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (!this.lightbox.classList.contains("active")) return;

        if (e.key === "Escape") {
          this.close();
        } else if (e.key === "ArrowLeft") {
          this.prev();
        } else if (e.key === "ArrowRight") {
          this.next();
        }
      });

      // Touch swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      this.lightbox.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true },
      );

      this.lightbox.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          this.handleSwipe();
        },
        { passive: true },
      );
    }

    open(index) {
      if (this.images.length === 0) return;

      this.currentIndex = index;
      this.lightbox.classList.add("active");
      this.lightbox.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden"; // Prevent body scroll

      // Set initial image and fade in
      const image = this.images[this.currentIndex];
      if (image) {
        this.lightboxImage.src = image.src;
        this.lightboxImage.alt = image.alt;
        // Trigger fade in after image loads
        this.lightboxImage.onload = () => {
          this.lightboxImage.classList.add("fade-in");
        };
        // If image is already cached, trigger immediately
        if (this.lightboxImage.complete) {
          this.lightboxImage.classList.add("fade-in");
        }
      }

      this.updateCount();

      // Focus trap
      const closeButton = this.lightbox.querySelector("[data-lightbox-close]");
      if (closeButton) {
        closeButton.focus();
      }
    }

    close() {
      this.lightbox.classList.remove("active");
      this.lightbox.setAttribute("aria-hidden", "true");
      document.body.style.overflow = ""; // Restore body scroll
    }

    prev() {
      if (this.images.length === 0) return;
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.updateImage();
      this.updateCount();
    }

    next() {
      if (this.images.length === 0) return;
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.updateImage();
      this.updateCount();
    }

    updateImage() {
      const image = this.images[this.currentIndex];
      if (!image) return;

      // Fade out current image
      this.lightboxImage.classList.remove("fade-in");

      // Wait for fade out, then update and fade in
      setTimeout(() => {
        this.lightboxImage.src = image.src;
        this.lightboxImage.alt = image.alt;

        // Fade in new image
        setTimeout(() => {
          this.lightboxImage.classList.add("fade-in");
        }, 10);
      }, 150); // Half of transition duration for smoother effect

      // Update navigation buttons
      const prevButton = document.querySelector("[data-lightbox-prev]");
      const nextButton = document.querySelector("[data-lightbox-next]");

      if (prevButton) {
        prevButton.disabled = this.images.length <= 1;
      }
      if (nextButton) {
        nextButton.disabled = this.images.length <= 1;
      }
    }

    updateCount() {
      if (this.lightboxCount && this.images.length > 0) {
        this.lightboxCount.textContent = `${this.currentIndex + 1}/${this.images.length}`;
      }
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }
  }

  class ProductForm {
    constructor() {
      this.form = document.querySelector("#product-form, #product-form-simple");
      if (!this.form) return;

      this.product = window.productData || {};
      this.variantIdInput = this.form.querySelector("[data-variant-id]");
      this.sellingPlanInput = this.form.querySelector(
        "#selling-plan-input, #selling-plan-input-simple",
      );
      this.priceElements = document.querySelectorAll("[data-product-price]");
      this.comparePriceElements = document.querySelectorAll(".product__price-compare");
      this.onetimePriceElements = document.querySelectorAll("[data-onetime-price]");
      this.subscribePriceElements = document.querySelectorAll("[data-subscribe-price]");
      this.addToCartButton = this.form.querySelector(".product__add-to-cart");
      this.quantityInput = this.form.querySelector("[data-quantity-input]");
      this.currentVariant = null;
      this.activeDescriptionTooltip = null;

      this.init();
    }

    init() {
      // Handle variant selection (buttons)
      const variantButtons = document.querySelectorAll(".product__option-value");
      variantButtons.forEach((button) => {
        button.addEventListener("click", this.handleVariantSelection.bind(this));
      });

      // Handle custom size dropdown
      this.initSizeDropdown();

      // Handle native select for mobile
      this.initNativeSelect();

      // Initialize variant description tooltips
      this.initVariantDescriptions();

      // Check if there's a pre-selected variant from URL parameter (e.g., from Opus collection)
      const preselectedVariantId = this.variantIdInput ? parseInt(this.variantIdInput.value) : null;
      let preselectedVariant = null;

      if (preselectedVariantId) {
        preselectedVariant = this.product.variants?.find((v) => v.id === preselectedVariantId);
      }

      // Initialize first variant as selected
      const sizeDropdownToggle = document.querySelector("[data-size-dropdown-toggle]");
      if (variantButtons.length > 0 || sizeDropdownToggle) {
        // If we have a pre-selected variant from URL, select its options
        if (preselectedVariant) {
          // Activate the corresponding option buttons for the pre-selected variant
          const allOptionButtons = document.querySelectorAll(".product__option-value");
          allOptionButtons.forEach((btn) => {
            const position = btn.dataset.optionPosition;
            const value = btn.dataset.optionValue;
            const variantOption = preselectedVariant[`option${position}`];

            if (value === variantOption) {
              btn.classList.add("active");
            }
          });

          // If there's a size dropdown, set it to the pre-selected variant's size
          if (sizeDropdownToggle) {
            const sizePosition = sizeDropdownToggle.dataset.optionPosition;
            const preselectedSize = preselectedVariant[`option${sizePosition}`];
            const valueDisplay = sizeDropdownToggle.querySelector("[data-size-dropdown-value]");
            if (valueDisplay && preselectedSize) {
              valueDisplay.textContent = preselectedSize;
            }
          }
        } else {
          // No pre-selected variant - use default selection logic
          // Handle button-based options (e.g., Scent)
          const firstOption = document.querySelector(".product__option-values");
          if (firstOption) {
            let defaultButton = null;

            // For fragrance products, try to find and select 15mL roll-on variant by default
            if (this.isFragranceProduct()) {
              const buttons = firstOption.querySelectorAll(".product__option-value");
              buttons.forEach((btn) => {
                const value = btn.dataset.optionValue;
                if (value) {
                  const valueLower = value.toLowerCase().replace(/\s+/g, "");
                  // Match variants like "15ml", "15 ml", "15mL Roll-On", "15 ml roll on", etc.
                  if (valueLower.includes("15") && valueLower.includes("ml")) {
                    defaultButton = btn;
                  }
                }
              });
            }

            // If no 15mL found or not a fragrance product, use first button
            if (!defaultButton) {
              defaultButton = firstOption.querySelector(".product__option-value");
            }

            if (defaultButton && !document.querySelector(".product__option-value.active")) {
              defaultButton.classList.add("active");
            }
          }

          // Handle custom size dropdown (prefer 15mL for fragrance products)
          if (sizeDropdownToggle && this.isFragranceProduct()) {
            const sizeOptions = document.querySelectorAll("[data-size-option]");
            let default15mlOption = null;

            sizeOptions.forEach((option) => {
              const value = option.dataset.optionValue || "";
              const valueLower = value.toLowerCase().replace(/\s+/g, "");
              if (valueLower.includes("15") && valueLower.includes("ml")) {
                default15mlOption = option;
              }
            });

            if (default15mlOption) {
              // Update the toggle text to show 15ml as default
              const valueDisplay = sizeDropdownToggle.querySelector("[data-size-dropdown-value]");
              if (valueDisplay) {
                valueDisplay.textContent = default15mlOption.dataset.optionValue;
              }
            }
          }
        }

        // Initialize prices with first variant
        this.updateVariant();

        // Initialize variant description display after variants are set up
        this.initVariantDescriptionDisplay();
      } else {
        // No variants, use default variant
        const variants = this.product.variants || [];
        if (variants.length > 0) {
          this.currentVariant = variants[0];
          this.updatePrices(variants[0]);
        }
      }

      // Handle quantity controls
      const decreaseButton = this.form.querySelector("[data-quantity-decrease]");
      const increaseButton = this.form.querySelector("[data-quantity-increase]");

      if (decreaseButton) {
        decreaseButton.addEventListener("click", this.decreaseQuantity.bind(this));
      }

      if (increaseButton) {
        increaseButton.addEventListener("click", this.increaseQuantity.bind(this));
      }

      // Handle form submission
      this.form.addEventListener("submit", this.handleSubmit.bind(this));

      // Handle subscription toggle
      this.initSubscriptionToggle();

      // Initialize prices on page load
      this.initializePrices();
    }

    initializePrices() {
      // If variant is already set, update prices
      if (this.currentVariant) {
        this.updatePrices(this.currentVariant);
      } else {
        // Try to get variant from URL or use first variant
        const urlParams = new URLSearchParams(window.location.search);
        const variantIdFromUrl = urlParams.get("variant");

        if (variantIdFromUrl) {
          const variants = this.product.variants || [];
          const variant = variants.find((v) => v.id.toString() === variantIdFromUrl.toString());
          if (variant) {
            this.currentVariant = variant;
            this.updatePrices(variant);
            return;
          }
        }

        // Use first variant as fallback
        const variants = this.product.variants || [];
        if (variants.length > 0) {
          this.currentVariant = variants[0];
          this.updatePrices(variants[0]);
        }
      }

      // Always initialize subscription price on page load (not just when subscribe is selected)
      if (this.currentVariant) {
        this.updateSubscriptionPrice();
      }
    }

    initSizeDropdown() {
      const dropdownToggle = document.querySelector("[data-size-dropdown-toggle]");
      if (!dropdownToggle) return;

      const dropdown = document.querySelector("[data-size-dropdown]");
      const sizeOptions = document.querySelectorAll("[data-size-option]");
      const valueDisplay = dropdownToggle.querySelector("[data-size-dropdown-value]");

      if (!dropdown || sizeOptions.length === 0) return;

      // Toggle dropdown open/close
      dropdownToggle.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = dropdown.hasAttribute("hidden");

        if (isOpen) {
          dropdown.removeAttribute("hidden");
          dropdownToggle.setAttribute("aria-expanded", "true");
        } else {
          dropdown.setAttribute("hidden", "");
          dropdownToggle.setAttribute("aria-expanded", "false");
        }
      });

      // Handle option selection
      sizeOptions.forEach((option) => {
        option.addEventListener("click", (e) => {
          e.stopPropagation();
          const value = option.dataset.optionValue;

          // Remove selected class from all options
          sizeOptions.forEach((opt) => opt.classList.remove("selected"));

          // Add selected class to clicked option
          option.classList.add("selected");

          // Update the toggle display
          if (valueDisplay) {
            valueDisplay.textContent = value;
          }

          // Sync with native select (if present)
          const nativeSelect = document.querySelector("[data-size-select-native]");
          if (nativeSelect) {
            nativeSelect.value = value;
          }

          // Close dropdown
          dropdown.setAttribute("hidden", "");
          dropdownToggle.setAttribute("aria-expanded", "false");

          // Update variant
          this.updateVariant();
        });
      });

      // Close dropdown when clicking outside
      document.addEventListener("click", (e) => {
        if (!dropdownToggle.contains(e.target) && !dropdown.contains(e.target)) {
          dropdown.setAttribute("hidden", "");
          dropdownToggle.setAttribute("aria-expanded", "false");
        }
      });

      // Initialize first option as selected
      if (sizeOptions.length > 0) {
        const currentValue = valueDisplay ? valueDisplay.textContent.trim() : "";
        sizeOptions.forEach((option) => {
          if (option.dataset.optionValue === currentValue) {
            option.classList.add("selected");
          }
        });
      }
    }

    initNativeSelect() {
      const nativeSelect = document.querySelector("[data-size-select-native]");
      if (!nativeSelect) return;

      // Handle native select change (mobile)
      nativeSelect.addEventListener("change", (e) => {
        const value = e.target.value;

        // Sync with custom dropdown elements (for desktop view)
        const valueDisplay = document.querySelector("[data-size-dropdown-value]");
        if (valueDisplay) {
          valueDisplay.textContent = value;
        }

        const sizeOptions = document.querySelectorAll("[data-size-option]");
        sizeOptions.forEach((option) => {
          if (option.dataset.optionValue === value) {
            option.classList.add("selected");
          } else {
            option.classList.remove("selected");
          }
        });

        // Update variant
        this.updateVariant();
      });
    }

    initVariantDescriptions() {
      // No tooltip initialization - descriptions show in container below buttons
      // This method can be removed but keeping for compatibility
    }

    initVariantDescriptionDisplay() {
      // Show description for initially active/selected variant
      const activeButton = document.querySelector(
        ".product__option-value.active[data-variant-description]",
      );
      if (activeButton) {
        this.updateMobileVariantDescription(activeButton);
      }
    }

    showVariantDescription(tooltip, button) {
      // Only show tooltip on desktop (not on mobile/tablet)
      if (window.innerWidth < 1024) return;

      // Hide any currently active tooltip
      if (this.activeDescriptionTooltip && this.activeDescriptionTooltip !== tooltip) {
        this.hideVariantDescription(this.activeDescriptionTooltip);
      }

      this.positionVariantDescription(tooltip, button);
      tooltip.classList.add("visible");
      this.activeDescriptionTooltip = tooltip;
    }

    positionVariantDescription(tooltip, button) {
      // Get button position
      const rect = button.getBoundingClientRect();

      // First, get tooltip dimensions by making it temporarily visible
      const wasVisible = tooltip.classList.contains("visible");
      if (!wasVisible) {
        tooltip.style.visibility = "hidden";
        tooltip.style.display = "block";
      }
      const tooltipRect = tooltip.getBoundingClientRect();
      if (!wasVisible) {
        tooltip.style.display = "";
        tooltip.style.visibility = "";
      }

      // Position tooltip with TOP-LEFT corner at the center of the button
      const x = rect.left + rect.width / 2; // Horizontal center of button
      const y = rect.top + rect.height / 2; // Vertical center of button

      // Set position (top-left corner at calculated x, y)
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;

      // Ensure tooltip stays within viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Adjust horizontal position if tooltip goes off screen
      const newTooltipRect = tooltip.getBoundingClientRect();
      if (newTooltipRect.right > viewportWidth - 10) {
        tooltip.style.left = `${viewportWidth - tooltipRect.width - 10}px`;
      }
      if (newTooltipRect.left < 10) {
        tooltip.style.left = "10px";
      }

      // Adjust vertical position if tooltip goes off screen
      if (y < 10) {
        tooltip.style.top = `${rect.bottom + 8}px`; // Show below button if no room
      }
    }

    hideVariantDescription(tooltip) {
      tooltip.classList.remove("visible");
      if (this.activeDescriptionTooltip === tooltip) {
        this.activeDescriptionTooltip = null;
      }
    }

    isFragranceProduct() {
      // Check if product is a fragrance based on title or product type
      const title = this.product.title || "";
      const productType = window.productType || "";

      // Check if title contains fragrance keywords
      const fragranceKeywords = ["opus", "eau de parfum", "edp", "fragrance", "perfume", "cologne"];
      const titleLower = title.toLowerCase();

      for (const keyword of fragranceKeywords) {
        if (titleLower.includes(keyword)) {
          return true;
        }
      }

      // Check product type
      if (productType.toLowerCase().includes("fragrance")) {
        return true;
      }

      return false;
    }

    initSubscriptionToggle() {
      const purchaseTypeRadios = this.form.querySelectorAll('[name="purchase_type"]');
      const subscriptionDetails = this.form.querySelector("[data-subscription-details]");
      const sellingPlanRadios = this.form.querySelectorAll('[name="selling_plan"]');

      if (!purchaseTypeRadios.length || !subscriptionDetails) return;

      purchaseTypeRadios.forEach((radio) => {
        radio.addEventListener("change", () => {
          if (radio.value === "subscribe") {
            subscriptionDetails.removeAttribute("hidden");
            // Select first frequency option by default
            const firstFrequency = subscriptionDetails.querySelector('[name="selling_plan"]');
            if (
              firstFrequency &&
              !subscriptionDetails.querySelector('[name="selling_plan"]:checked')
            ) {
              firstFrequency.checked = true;
              if (this.sellingPlanInput) {
                this.sellingPlanInput.value = firstFrequency.value;
              }
            }
            // Update subscription price immediately when subscribe is selected
            this.updateSubscriptionPrice();
          } else {
            subscriptionDetails.setAttribute("hidden", "");
            // Clear selling plan selection
            sellingPlanRadios.forEach((planRadio) => (planRadio.checked = false));
            if (this.sellingPlanInput) {
              this.sellingPlanInput.value = "";
            }
          }
        });
      });

      // Handle frequency selection
      sellingPlanRadios.forEach((radio) => {
        radio.addEventListener("change", () => {
          if (radio.checked && this.sellingPlanInput) {
            this.sellingPlanInput.value = radio.value;
          }
          // Update subscription price when plan changes
          this.updateSubscriptionPrice();
        });
      });
    }

    handleVariantSelection(e) {
      const button = e.currentTarget;
      const position = button.dataset.optionPosition;
      const value = button.dataset.optionValue;

      // Toggle active state
      const siblings = button.parentElement.querySelectorAll(".product__option-value");
      siblings.forEach((sib) => sib.classList.remove("active"));
      button.classList.add("active");

      // Update mobile variant description if on mobile/tablet
      this.updateMobileVariantDescription(button);

      // Update size dropdown prices when scent changes
      this.updateSizeDropdownPrices();

      // Update variant based on all selected options
      this.updateVariant();
    }

    updateMobileVariantDescription(button) {
      // Update description container on all devices (not just mobile)
      const descriptionContainer = document.getElementById("variant-description");
      if (!descriptionContainer) {
        console.log("[Variant Description] Container not found in DOM");
        return;
      }

      // Check if this button has a description
      const description = button.dataset.variantDescription;

      // Update text content - container always visible now
      descriptionContainer.textContent = description || "";
      console.log("[Variant Description] Updated:", description || "empty");
    }

    updateSizeDropdownPrices() {
      // Get currently selected scent (or other non-size option)
      const selectedScentButton = document.querySelector(".product__option-value.active");
      if (!selectedScentButton) return;

      const selectedScent = selectedScentButton.dataset.optionValue;
      if (!selectedScent) return;

      // Update each size option with the price for the selected scent
      const sizeOptions = document.querySelectorAll("[data-size-option]");
      const variants = this.product.variants || [];

      sizeOptions.forEach((option) => {
        const sizeValue = option.dataset.optionValue;
        const priceElement = option.querySelector("[data-size-price]");

        if (!priceElement || !sizeValue) return;

        // Find variant that matches both scent and size
        const matchingVariant = variants.find((variant) => {
          return variant.title.includes(selectedScent) && variant.title.includes(sizeValue);
        });

        if (matchingVariant) {
          priceElement.textContent = this.formatMoney(matchingVariant.price);
          option.dataset.basePrice = matchingVariant.price;
        }
      });
    }

    updateVariant() {
      const selectedOptions = {};

      // Get selected values from button-based options
      const optionButtons = document.querySelectorAll(".product__option-value.active");
      optionButtons.forEach((button) => {
        const position = button.dataset.optionPosition;
        const value = button.dataset.optionValue;
        selectedOptions[`option${position}`] = value;
      });

      // Get selected value from native select (mobile) or custom dropdown (desktop)
      const nativeSelect = document.querySelector("[data-size-select-native]");
      const sizeDropdownToggle = document.querySelector("[data-size-dropdown-toggle]");

      if (nativeSelect && window.innerWidth < 768) {
        // Use native select on mobile
        const position = nativeSelect.dataset.optionPosition;
        const value = nativeSelect.value;
        if (value) {
          selectedOptions[`option${position}`] = value;
        }
      } else if (sizeDropdownToggle) {
        // Use custom dropdown on desktop
        const position = sizeDropdownToggle.dataset.optionPosition;
        const valueDisplay = sizeDropdownToggle.querySelector("[data-size-dropdown-value]");
        if (valueDisplay) {
          const value = valueDisplay.textContent.trim();
          selectedOptions[`option${position}`] = value;
        }
      }

      // Find matching variant
      const variants = window.productData?.variants || [];
      let matchingVariant = null;

      // First try to find exact match with all selected options
      if (Object.keys(selectedOptions).length > 0) {
        matchingVariant = variants.find((variant) => {
          return Object.keys(selectedOptions).every((key) => {
            return variant[key] === selectedOptions[key];
          });
        });
      }

      // If no exact match and we have some selections, try to find first available variant
      // that matches our selected options (for products with multiple option groups)
      if (!matchingVariant && Object.keys(selectedOptions).length > 0) {
        matchingVariant = variants.find((variant) => {
          return Object.keys(selectedOptions).every((key) => {
            return variant[key] === selectedOptions[key];
          });
        });
      }

      // If still no match, use first available variant as fallback
      if (!matchingVariant) {
        matchingVariant = variants.find((variant) => variant.available) || variants[0];
      }

      if (matchingVariant) {
        // Store current variant
        this.currentVariant = matchingVariant;

        // Update form
        if (this.variantIdInput) {
          this.variantIdInput.value = matchingVariant.id;
        }

        // Update all prices
        this.updatePrices(matchingVariant);

        // Update add to cart button
        if (this.addToCartButton) {
          const buttonText = this.addToCartButton.querySelector(".product__add-to-cart-text");
          if (matchingVariant.available) {
            this.addToCartButton.disabled = false;
            if (buttonText) {
              buttonText.textContent = "ADD TO CART";
            }
          } else {
            this.addToCartButton.disabled = true;
            if (buttonText) {
              buttonText.textContent = "SOLD OUT";
            }
          }
        }

        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set("variant", matchingVariant.id);
        window.history.replaceState({}, "", url);
      }
    }

    updatePrices(variant) {
      if (!variant) return;

      // Update all main product price elements
      this.priceElements.forEach((element) => {
        element.textContent = this.formatMoney(variant.price);
      });

      // Update all compare at price elements
      this.comparePriceElements.forEach((element) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          element.textContent = this.formatMoney(variant.compare_at_price);
          element.style.display = "";
        } else {
          element.style.display = "none";
        }
      });

      // Update all one-time purchase price elements
      this.onetimePriceElements.forEach((element) => {
        element.textContent = this.formatMoney(variant.price);
      });

      // Update subscription price with the current variant
      this.updateSubscriptionPrice(variant);
    }

    updateSubscriptionPrice(variant = null) {
      // Use provided variant or fall back to currentVariant
      const targetVariant = variant || this.currentVariant;
      if (
        !targetVariant ||
        !this.subscribePriceElements ||
        this.subscribePriceElements.length === 0
      )
        return;

      // Always show the discounted subscription price by default (not just when subscribe is selected)
      // Get selected selling plan, or use first one if none selected
      let selectedSellingPlanId = this.sellingPlanInput?.value;

      // If no plan selected, use first available plan for price calculation
      if (!selectedSellingPlanId) {
        const subscriptionDetails = this.form.querySelector("[data-subscription-details]");
        if (subscriptionDetails) {
          const firstFrequency = subscriptionDetails.querySelector('[name="selling_plan"]');
          if (firstFrequency) {
            selectedSellingPlanId = firstFrequency.value;
          }
        }
      }

      // If still no plan, try to get first selling plan from data
      if (!selectedSellingPlanId) {
        const sellingPlans = this.product.selling_plans || [];
        if (sellingPlans.length > 0) {
          selectedSellingPlanId = sellingPlans[0].id.toString();
        }
      }

      if (!selectedSellingPlanId) {
        // No plan available, show regular price
        this.subscribePriceElements.forEach((element) => {
          element.textContent = this.formatMoney(targetVariant.price);
        });
        return;
      }

      // Find selling plan
      const sellingPlans = this.product.selling_plans || [];
      const sellingPlan = sellingPlans.find(
        (plan) => plan.id.toString() === selectedSellingPlanId.toString(),
      );

      if (
        !sellingPlan ||
        !sellingPlan.price_adjustments ||
        sellingPlan.price_adjustments.length === 0
      ) {
        // No price adjustment, show regular price
        this.subscribePriceElements.forEach((element) => {
          element.textContent = this.formatMoney(targetVariant.price);
        });
        return;
      }

      // Calculate subscription price based on price adjustment
      const adjustment = sellingPlan.price_adjustments[0];
      let subscriptionPrice = targetVariant.price;

      if (adjustment.value_type === "percentage") {
        // Percentage discount
        const discountAmount = Math.round(targetVariant.price * (adjustment.value / 100));
        subscriptionPrice = targetVariant.price - discountAmount;
      } else if (adjustment.value_type === "fixed_amount") {
        // Fixed amount discount
        const discountAmount = Math.round(adjustment.value * 100); // Convert to cents
        subscriptionPrice = Math.max(0, targetVariant.price - discountAmount);
      } else if (adjustment.value_type === "price") {
        // Fixed price
        subscriptionPrice = Math.round(adjustment.value * 100); // Convert to cents
      }

      // Update all subscription price elements with discounted price
      this.subscribePriceElements.forEach((element) => {
        element.textContent = this.formatMoney(subscriptionPrice);
      });
    }

    decreaseQuantity() {
      if (this.quantityInput) {
        const currentValue = parseInt(this.quantityInput.value) || 1;
        const newValue = Math.max(1, currentValue - 1);
        this.quantityInput.value = newValue;
      }
    }

    increaseQuantity() {
      if (this.quantityInput) {
        const currentValue = parseInt(this.quantityInput.value) || 1;
        this.quantityInput.value = currentValue + 1;
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }

    async handleSubmit(e) {
      e.preventDefault();

      const formData = new FormData(this.form);
      const data = {};

      formData.forEach((value, key) => {
        data[key] = value;
      });

      // Ensure selling_plan is cleared if one-time purchase is selected
      const purchaseType = this.form.querySelector('[name="purchase_type"]:checked');
      if (purchaseType && purchaseType.value === "onetime") {
        data.selling_plan = "";
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = "";
        }
      }

      try {
        const buttonText = this.addToCartButton.querySelector(".product__add-to-cart-text");
        const originalText = buttonText ? buttonText.textContent : "ADD TO CART";

        this.addToCartButton.disabled = true;
        if (buttonText) {
          buttonText.textContent = "ADDING...";
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: data.id,
                quantity: parseInt(data.quantity) || 1,
                selling_plan: data.selling_plan || null,
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to add to cart");
        }

        const result = await response.json();

        // Update cart count
        this.updateCartCount();

        // Success feedback
        if (buttonText) {
          buttonText.textContent = "ADDED!";
        }

        setTimeout(() => {
          this.addToCartButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = originalText;
          }
        }, 2000);

        // Dispatch event
        document.dispatchEvent(
          new CustomEvent("cart:item-added", {
            detail: result,
          }),
        );
      } catch (error) {
        console.error("Error adding to cart:", error);
        const buttonText = this.addToCartButton.querySelector(".product__add-to-cart-text");

        this.addToCartButton.disabled = false;
        if (buttonText) {
          buttonText.textContent = "ERROR - TRY AGAIN";

          setTimeout(() => {
            buttonText.textContent = "ADD TO CART";
          }, 3000);
        }
      }
    }

    async updateCartCount() {
      // Use centralized cart count updater from theme.js
      if (window.theme && window.theme.updateCartCount) {
        const count = await window.theme.updateCartCount();

        // Dispatch cart updated event for other listeners
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { item_count: count },
          }),
        );
      } else {
        console.warn("theme.updateCartCount not available");
      }
    }
  }

  /**
   * Accordion Class
   * Handles expanding/collapsing accordion sections
   */
  class ProductAccordion {
    constructor() {
      this.triggers = document.querySelectorAll(".product__accordion-trigger");
      if (this.triggers.length === 0) return;

      this.init();
    }

    init() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", this.handleToggle.bind(this));
      });
    }

    handleToggle(e) {
      const trigger = e.currentTarget;
      const isExpanded = trigger.getAttribute("aria-expanded") === "true";

      // Toggle the aria-expanded attribute
      trigger.setAttribute("aria-expanded", !isExpanded);

      // Find the associated content
      const contentId = trigger.getAttribute("aria-controls");
      const content = document.getElementById(contentId);

      if (content) {
        if (!isExpanded) {
          // Expand
          content.removeAttribute("hidden");
        } else {
          // Collapse
          content.setAttribute("hidden", "");
        }
      }
    }
  }

  // Check URL for purchase_option parameter and pre-select subscription
  function preselectPurchaseOption() {
    const urlParams = new URLSearchParams(window.location.search);
    const purchaseOption = urlParams.get("purchase_option");

    if (purchaseOption === "subscription") {
      // Find and select the subscription radio button (value is "subscribe" not "subscription")
      const subscribeRadio = document.querySelector(
        'input[name="purchase_type"][value="subscribe"]',
      );
      if (subscribeRadio) {
        subscribeRadio.checked = true;

        // Trigger change event to show subscription details
        const changeEvent = new Event("change", { bubbles: true });
        subscribeRadio.dispatchEvent(changeEvent);

        // Show subscription details panel
        const subscriptionDetails = document.querySelector("[data-subscription-details]");
        if (subscriptionDetails) {
          subscriptionDetails.removeAttribute("hidden");
        }

        // Optionally scroll to the subscription section
        setTimeout(() => {
          const purchaseOptions = document.querySelector(".product__purchase-options");
          if (purchaseOptions) {
            purchaseOptions.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      new ProductLightbox();
      new ProductForm();
      new ProductAccordion();
      preselectPurchaseOption();
    });
  } else {
    new ProductLightbox();
    new ProductForm();
    new ProductAccordion();
    preselectPurchaseOption();
  }

  /**
   * Remove subscription app widgets injected by external apps
   * These widgets are injected via content_for_header and need to be removed from DOM
   */
  function removeSubscriptionAppWidgets() {
    // Selectors for common subscription app widgets
    const selectors = [
      ".subscriptions_app_embed_block",
      '[class*="subscriptions_app"]',
      '[class*="recharge-widget"]',
      "[data-subscription-widget]",
      "#subscription-widget",
    ];

    selectors.forEach((selector) => {
      try {
        const widgets = document.querySelectorAll(selector);
        widgets.forEach((widget) => {
          // Only remove if it's not part of our custom subscription UI
          if (!widget.closest(".product__purchase-options")) {
            widget.remove();
          }
        });
      } catch (e) {
        // Silently fail if selector is invalid
      }
    });
  }

  // Remove widgets on page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", removeSubscriptionAppWidgets);
  } else {
    removeSubscriptionAppWidgets();
  }

  // Also remove widgets after a delay to catch dynamically injected ones
  setTimeout(removeSubscriptionAppWidgets, 1000);
  setTimeout(removeSubscriptionAppWidgets, 3000);
})();
